import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Pagination } from 'react-bootstrap';
import axios from 'axios';
import { API_URL } from '../../services/api';
import { useCookies } from "react-cookie";


const StatisticsModal = ({ show, handleClose, companyId }) => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [formId, setFormId] = useState('');
    const [forms, setForms] = useState([]);
    const [statistics, setStatistics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [token, setToken] = useCookies(["token"]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortedStatistics, setSortedStatistics] = useState([]);


    useEffect(() => {
        if (show) {
            setStartDate('');
            setEndDate('');
            setFormId('');
            setStatistics([]);
            setSortedStatistics([]);
            setCurrentPage(1);
            setSortOrder('desc');
            
            if (companyId) {
                loadForms();
            }
        }
    }, [show, companyId]);

    useEffect(() => {
        const sorted = [...statistics].sort((a, b) => {
            return sortOrder === 'asc' 
                ? a.total_send - b.total_send 
                : b.total_send - a.total_send;
        });
        setSortedStatistics(sorted);
    }, [statistics, sortOrder]);

    const loadForms = async () => {
        try {
            const headers = {
                headers: {
                    Authorization: `Bearer ${token.token}`,
                },
            };
            const response = await axios.get(`${API_URL}companies/${companyId}/forms`, headers);
            setForms(response.data);
        } catch (error) {
            console.error('Error al cargar formularios:', error);
            setForms([]);
        }
    };

    const fetchStatistics = async () => {
        if (!startDate || !endDate) return;
        
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}companies/${companyId}/statistics`, {
                headers: {
                    Authorization: `Bearer ${token.token}`,
                },
                params: {
                    start_date: startDate,
                    end_date: endDate,
                    form_id: formId
                }
            });
            setStatistics(Array.isArray(response.data.statistics) ? response.data.statistics : []);
        } catch (error) {
            console.error('Error al obtener estadísticas:', error);
            setStatistics([]);
        } finally {
            setLoading(false);
        }
    };

    const downloadExcel = async () => {
        if (!startDate || !endDate) return;
        
        setDownloading(true);
        try {
            const response = await axios.get(
                `${API_URL}companies/${companyId}/statistics/export`, 
                {
                    headers: {
                        Authorization: `Bearer ${token.token}`,
                    },
                    params: {
                        start_date: startDate,
                        end_date: endDate,
                        form_id: formId
                    },
                    responseType: 'blob'
                }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `estadisticas_${startDate}_${endDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error al descargar el excel:', error);
        } finally {
            setDownloading(false);
        }
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedStatistics.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedStatistics.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const toggleSortOrder = () => {
        setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Estadísticas de Formularios</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form className="mb-4">
                    <div className="d-flex gap-3 flex-wrap">
                        <Form.Group className="flex-grow-1">
                            <Form.Label>Formulario</Form.Label>
                            <Form.Select
                                value={formId}
                                onChange={(e) => setFormId(e.target.value)}
                            >
                                <option value="">Todos los formularios</option>
                                {forms.map((form) => (
                                    <option key={form.id} value={form.id}>
                                        {form.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="flex-grow-1">
                            <Form.Label>Fecha Inicio</Form.Label>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="flex-grow-1">
                            <Form.Label>Fecha Final</Form.Label>
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </Form.Group>
                        <div className="d-flex align-items-end gap-2">
                            <Button 
                                variant="primary" 
                                onClick={fetchStatistics}
                                disabled={!startDate || !endDate || loading}
                            >
                                Buscar
                            </Button>
                            <Button 
                                variant="success" 
                                onClick={downloadExcel}
                                disabled={!startDate || !endDate || downloading || !statistics.length}
                            >
                                {downloading ? 'Descargando...' : 'Descargar Excel'}
                            </Button>
                        </div>
                    </div>
                </Form>

                {loading ? (
                    <div className="text-center">Cargando...</div>
                ) : (
                    <>
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Formulario</th>
                                    <th style={{ cursor: 'pointer' }} onClick={toggleSortOrder}>
                                        Cantidad de Envíos {' '}
                                        {sortOrder === 'asc' ? '↑' : '↓'}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((stat) => (
                                    <tr key={stat.form_id}>
                                        <td>{stat.form_name}</td>
                                        <td>{stat.total_send}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {statistics.length > 0 && (
                            <div className="d-flex justify-content-center mt-3">
                                <Pagination>
                                    <Pagination.First 
                                        onClick={() => handlePageChange(1)}
                                        disabled={currentPage === 1}
                                    />
                                    <Pagination.Prev 
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    />
                                    
                                    {[...Array(totalPages)].map((_, index) => (
                                        <Pagination.Item
                                            key={index + 1}
                                            active={currentPage === index + 1}
                                            onClick={() => handlePageChange(index + 1)}
                                        >
                                            {index + 1}
                                        </Pagination.Item>
                                    ))}

                                    <Pagination.Next 
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    />
                                    <Pagination.Last 
                                        onClick={() => handlePageChange(totalPages)}
                                        disabled={currentPage === totalPages}
                                    />
                                </Pagination>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default StatisticsModal; 